import config from 'config'
import { createLogic } from 'redux-logic'
import { fetchAction, getDateForRequest } from 'utils'

import * as constants from './constants'
import * as selectors from './selectors'

const { RANKINGS } = constants

const getUrlPerRanking = ranking => {
  switch (ranking) {
    case RANKINGS.CLUB_CARD:
    case RANKINGS.COMPANIES_BY_CHANNELS:
    case RANKINGS.COMPANIES_ECOMMERCE:
    case RANKINGS.COMPANIES_STATIONARY:
      return 'rankingCountries'

    case RANKINGS.DISTRICT:
      return 'rankingDistricts'

    case RANKINGS.RKS:
      return 'rankingRegions'

    case RANKINGS.STORES_OPEN_WITHIN_12_MONTHS:
    case RANKINGS.STORES_OPEN_WITHIN_30_DAYS:
    case RANKINGS.BEST_20_SHOPS:
    case RANKINGS.WEBSITE_ECOMMERCE:
    case RANKINGS.WORST_20_SHOPS:
      return 'rankingStores'

    default:
      return ''
  }
}

const getParamsPerRanking = ranking => {
  switch (ranking) {
    case RANKINGS.STORES_OPEN_WITHIN_12_MONTHS:
      return {
        ecoms: false,
        necoms: true,
        opened1yOnly: true,
      }

    case RANKINGS.STORES_OPEN_WITHIN_30_DAYS:
      return {
        ecoms: false,
        necoms: true,
        opened30dOnly: true,
      }

    case RANKINGS.BEST_20_SHOPS:
      return {
        ecoms: false,
        necoms: true,
        top: 20,
        desc: false,
      }

    case RANKINGS.WEBSITE_ECOMMERCE:
    case RANKINGS.COMPANIES_ECOMMERCE:
      return {
        ecoms: true,
        necoms: false,
      }

    case RANKINGS.WORST_20_SHOPS:
      return {
        desc: true,
        ecoms: false,
        necoms: true,
        top: 20,
      }

    case RANKINGS.COMPANIES_STATIONARY:
    case RANKINGS.CLUB_CARD:
      return {
        ecoms: false,
        necoms: true,
      }

    case RANKINGS.COMPANIES_BY_CHANNELS:
      return {
        ecoms: true,
        necoms: true,
      }

    default:
      return {}
  }
}

const getParamsPerIndicator = indicator => {
  switch (indicator) {
    case constants.INDICATORS.COMPANIES_NECOMMERCE:
      return {
        ecoms: false,
        necoms: true,
      }

    case constants.INDICATORS.COMPANIES_ECOMMERCE:
    case constants.INDICATORS.COMPANIES_ECOMMERCE_ORDERS:
      return {
        ecoms: true,
        necoms: false,
      }

    default:
      return {}
  }
}

const fetchRankingCountries = createLogic({
  type: [constants.FETCH_RANKINGS_COUNTRIES, constants.SET_SELECTED_COUNTRIES],
  latest: true,

  async process({ getState }, dispatch, done) {
    try {
      const state = getState()
      const { dateCurrent, dateCompared } = state.datePicker
      const indicator = selectors.indicator(state)
      const ranking = selectors.ranking(state)
      const url = getUrlPerRanking(ranking)
      const params = getParamsPerRanking(ranking)
      const countryIds = selectors.selectedCountries(state).join(',') || null

      const response = await fetchAction(config.api[url], {
        countryIds,
        dateCurrent: getDateForRequest(dateCurrent),
        dateCompared: getDateForRequest(dateCompared),
        rankingType: indicator,
        ...params,
      })

      dispatch({
        type: constants.FETCH_RANKINGS_COUNTRIES_SUCCESS,
        payload: response.data,
      })
    } catch (error) {
      dispatch({
        type: constants.FETCH_RANKINGS_COUNTRIES_ERROR,
      })
    }

    done()
  },
})

const transformPercent = (data, fieldName) =>
  data.map(countriesHourlyRevenue => ({
    ...countriesHourlyRevenue,
    countries: countriesHourlyRevenue.countries.map(country => ({
      ...country,
      revenuePerHour: country.revenuePerHour.map(hourData => ({
        hour: hourData.hour,
        value: hourData[fieldName] * 100,
      })),
    })),
  }))

const ensureCountriesOrder = (data, countryOrder) =>
  data.map(countriesHourlyRevenue => ({
    ...countriesHourlyRevenue,
    countries: countryOrder.map(countryId => countriesHourlyRevenue.countries.find(c => c.countryId === countryId)),
  }))

const fetchRankingHourlyCountries = createLogic({
  type: [constants.FETCH_RANKING_HOURLY_COUNTRIES, constants.SET_SELECTED_COUNTRY, constants.SET_SELECTED_COUNTRY_COMPARED],
  latest: true,

  async process({ getState }, dispatch, done) {
    try {
      const state = getState()
      const { dateCurrent, dateCompared } = state.datePicker
      const indicator = selectors.indicator(state)
      const ranking = selectors.ranking(state)
      const params = getParamsPerRanking(ranking)
      const indicatorParams = getParamsPerIndicator(indicator)
      const { isWeekly, selectedCountry, selectedCountryCompared } = state.rankings
      const selectedCountries = [selectedCountry, selectedCountryCompared].filter(Boolean)
      const countryIds = selectedCountries.join(',')
      const isPercentIndicator = constants.PERCENT_INDICATORS.includes(indicator)

      if (countryIds) {
        const response = await fetchAction(config.api.rankingHourlyCountries, {
          countryIds,
          dateCurrent: getDateForRequest(dateCurrent),
          dateCompared: getDateForRequest(dateCompared),
          rankingType: indicator,
          weekly: isPercentIndicator && isWeekly,
          ...params,
          ...indicatorParams,
        })

        const hourlyData =
          indicator === constants.INDICATORS.COMPANIES_ECOMMERCE_ORDERS
            ? transformPercent(response.data, 'ordersValueShare')
            : response.data

        const sortedHourlyData = selectedCountries.length > 1 ? ensureCountriesOrder(hourlyData, selectedCountries) : hourlyData

        dispatch({
          type: constants.FETCH_RANKING_HOURLY_COUNTRIES_SUCCESS,
          payload: sortedHourlyData,
        })
      } else {
        dispatch({
          type: constants.FETCH_RANKING_HOURLY_COUNTRIES_SUCCESS,
          payload: [],
        })
      }
    } catch (error) {
      dispatch({
        type: constants.FETCH_RANKING_HOURLY_COUNTRIES_ERROR,
      })
    }

    done()
  },
})

const transformStoreValues = data =>
  data.map(storeHourlyRevenue => ({
    ...storeHourlyRevenue,
    countries: storeHourlyRevenue.stores.map(store => store),
  }))

const ensureStoresOrder = (data, storesOrder) =>
  data.map(storesHourlyRevenue => ({
    ...storesHourlyRevenue,
    stores: storesOrder.map(storeId => storesHourlyRevenue.stores.find(c => c.storeId === storeId)),
  }))

const fetchRankingHourlyStores = createLogic({
  type: [constants.FETCH_RANKING_HOURLY_STORES, constants.SET_SELECTED_STORE, constants.SET_SELECTED_STORE_COMPARED],
  latest: true,

  async process({ getState }, dispatch, done) {
    try {
      const state = getState()
      const indicator = selectors.indicator(state)
      const { dateCurrent } = state.datePicker
      const { isWeekly, selectedStore, selectedStoreCompared } = state.rankings
      const selectedStores = [selectedStore, selectedStoreCompared].filter(Boolean)
      const storeIds = selectedStores.join(',')
      const isPercentIndicator = constants.PERCENT_INDICATORS.includes(indicator)

      if (storeIds) {
        const response = await fetchAction(config.api.rankingHourlyStores, {
          storeIds,
          dateCurrent: getDateForRequest(dateCurrent),
          weekly: isPercentIndicator && isWeekly,
        })
        const hourlyData = response.data
        const sortedHourlyData = selectedStores.length > 1 ? ensureStoresOrder(hourlyData, selectedStores) : hourlyData
        const sortedAndTransformedData = transformStoreValues(sortedHourlyData)
        let finalData = sortedAndTransformedData

        if (indicator === constants.INDICATORS.STORES_ECOMMERCE) {
          finalData = transformPercent(sortedAndTransformedData, 'revenueShare')
        }

        if (indicator === constants.INDICATORS.STORES_ECOMMERCE_ORDERS) {
          finalData = transformPercent(sortedAndTransformedData, 'ordersValueShare')
        }

        dispatch({
          type: constants.FETCH_RANKING_HOURLY_STORES_SUCCESS,
          payload: finalData,
        })
      } else {
        dispatch({
          type: constants.FETCH_RANKING_HOURLY_STORES_SUCCESS,
          payload: [],
        })
      }
    } catch (error) {
      dispatch({
        type: constants.FETCH_RANKING_HOURLY_STORES_ERROR,
      })
    }

    done()
  },
})

const refetchRankingData = createLogic({
  type: constants.TOGGLE_WEEKLY,
  latest: true,

  async process({ getState }, dispatch, done) {
    const state = getState()
    const { selectedStore } = state.rankings

    dispatch({
      type: selectedStore ? constants.FETCH_RANKING_HOURLY_STORES : constants.FETCH_RANKING_HOURLY_COUNTRIES,
    })
    done()
  },
})

export default [fetchRankingCountries, fetchRankingHourlyCountries, fetchRankingHourlyStores, refetchRankingData]
